import React, { useState, useRef, useEffect, useMemo, ChangeEvent } from 'react';
import { PaperPlaneIcon } from '@radix-ui/react-icons';
import { Link, useInRouterContext } from 'react-router-dom';
import { useOptionalOrganization } from '../../contexts/OrganizationContext';
import { useSavedReports } from '../reports/useSavedReports';
import { consumeChatPrefill, CHAT_PREFILL_EVENT } from './chatPrefill';
import type { ReportSummary } from '../../services/api';

// PHA-1168: `#` at a word boundary opens the saved-report picker.
const HASH_TRIGGER_RE = /(^|\s)#([A-Za-z0-9_-]*)$/;
const MAX_PICKER_ROWS = 8;

/** The `#partial` the caret is in, or null. Start is the index of the `#`. */
export function findReportTrigger(text: string, caret: number): { start: number; query: string } | null {
  const before = text.slice(0, caret);
  const m = HASH_TRIGGER_RE.exec(before);
  if (!m) return null;
  return { start: before.length - m[2].length - 1, query: m[2] };
}

interface ChatInputProps {
  onSend: (message: string) => void;
  disabled?: boolean;
  // PHA-1155: when set (an answer is being worked on), Send becomes Stop.
  onStop?: () => void;
  compact?: boolean;
  autoFocus?: boolean;
}

const ChatInput: React.FC<ChatInputProps> = ({ onSend, disabled, onStop, compact, autoFocus }) => {
  const [value, setValue] = useState('');
  const [stopRequested, setStopRequested] = useState(false);
  const [caret, setCaret] = useState(0);
  const [pickerDismissed, setPickerDismissed] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const pendingCaret = useRef<number | null>(null);
  const org = useOptionalOrganization();

  // A new answer starting resets the Stop button.
  useEffect(() => {
    if (!onStop) setStopRequested(false);
  }, [onStop]);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const trigger = useMemo(() => findReportTrigger(value, caret), [value, caret]);
  // Reports are fetched the first time `#` is typed, then shared from the cache.
  const [pickerWanted, setPickerWanted] = useState(false);
  useEffect(() => { if (trigger) setPickerWanted(true); }, [trigger]);
  const savedReports = useSavedReports(org?.orgId ?? '', { enabled: pickerWanted });
  const matches = useMemo<ReportSummary[]>(() => {
    if (!trigger) return [];
    const q = trigger.query.toLowerCase();
    return savedReports.reports
      .filter((r) => !q || r.handle.toLowerCase().includes(q) || r.name.toLowerCase().includes(q))
      .slice(0, MAX_PICKER_ROWS);
  }, [trigger, savedReports.reports]);
  const pickerOpen = !!trigger && !pickerDismissed && !disabled && !!org?.orgId;
  // Enter/Tab/arrows belong to the picker only while it has something to pick.
  const pickerActive = pickerOpen && matches.length > 0;

  useEffect(() => { setActiveIndex(0); }, [trigger?.query]);

  // A report page's "Ask Phanova" button leaves `#handle` here (see chatPrefill).
  useEffect(() => {
    const apply = (text: string) => {
      setValue(text);
      pendingCaret.current = text.length;
      setCaret(text.length);
      textareaRef.current?.focus();
    };
    const parked = consumeChatPrefill();
    if (parked) apply(parked);
    const onPrefill = (e: Event) => {
      const text = consumeChatPrefill();
      if (text) apply(text);
      else if (typeof (e as CustomEvent<string>).detail === 'string') apply((e as CustomEvent<string>).detail);
    };
    window.addEventListener(CHAT_PREFILL_EVENT, onPrefill);
    return () => window.removeEventListener(CHAT_PREFILL_EVENT, onPrefill);
  }, []);

  // Put the caret back after a programmatic value change (picking a report).
  useEffect(() => {
    const el = textareaRef.current;
    if (el && pendingCaret.current !== null) {
      el.setSelectionRange(pendingCaret.current, pendingCaret.current);
      pendingCaret.current = null;
    }
  }, [value]);

  const pickReport = (report: ReportSummary) => {
    if (!trigger) return;
    const after = value.slice(caret);
    const insert = `#${report.handle} `;
    const next = value.slice(0, trigger.start) + insert + after.replace(/^\s/, '');
    const nextCaret = trigger.start + insert.length;
    setValue(next);
    setCaret(nextCaret);
    pendingCaret.current = nextCaret;
    setPickerDismissed(false);
    textareaRef.current?.focus();
  };

  // Auto-resize textarea
  useEffect(() => {
    const el = textareaRef.current;
    if (el) {
      el.style.height = 'auto';
      el.style.height = Math.min(el.scrollHeight, 160) + 'px';
    }
  }, [value]);

  useEffect(() => {
    if (autoFocus) {
      textareaRef.current?.focus();
    }
  }, [autoFocus]);

  const handleSubmit = () => {
    const trimmed = value.trim();
    if (!trimmed || disabled) return;
    onSend(trimmed);
    setValue('');
    setCaret(0);
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (pickerActive) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIndex((i) => (i + 1) % matches.length);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIndex((i) => (i - 1 + matches.length) % matches.length);
        return;
      }
      if ((e.key === 'Enter' && !e.shiftKey) || e.key === 'Tab') {
        e.preventDefault();
        pickReport(matches[Math.min(activeIndex, matches.length - 1)]);
        return;
      }
    }
    if (pickerOpen && e.key === 'Escape') {
      e.preventDefault();
      setPickerDismissed(true);
      return;
    }
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setValue(e.target.value);
    setCaret(e.target.selectionStart ?? e.target.value.length);
    setPickerDismissed(false);
  };

  // Moving the caret with the mouse or Home/End changes which word is being typed.
  const syncCaret = (e: React.SyntheticEvent<HTMLTextAreaElement>) => {
    setCaret(e.currentTarget.selectionStart ?? e.currentTarget.value.length);
  };

  return (
    <div style={{
      padding: '12px 16px',
      borderTop: '1px solid #e2e8f0',
      backgroundColor: '#fff',
      position: 'relative',
    }}>
      {pickerOpen && (
        <ReportPicker
          reports={matches}
          activeIndex={activeIndex}
          loading={savedReports.loading}
          noReportsAtAll={!savedReports.loading && savedReports.reports.length === 0}
          canDesign={savedReports.canDesign}
          onHover={setActiveIndex}
          onPick={pickReport}
        />
      )}
      <div style={{
        border: '1px solid #d1d5db',
        borderRadius: 12,
        backgroundColor: '#fff',
        boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
        transition: 'box-shadow 0.15s ease, border-color 0.15s ease',
        overflow: 'hidden',
      }}
      onFocus={() => {
        const el = textareaRef.current?.parentElement;
        if (el) el.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)';
      }}
      onBlur={() => {
        const el = textareaRef.current?.parentElement;
        if (el) el.style.boxShadow = '0 1px 3px rgba(0,0,0,0.04)';
      }}
      >
        {/* Placeholder row with suggestion chips */}
        {!compact && (
          <div style={{
            display: 'flex',
            gap: 8,
            padding: '8px 12px 0',
            flexWrap: 'wrap',
          }}>
            <SuggestionChip onClick={onSend} disabled={disabled}>What can you do?</SuggestionChip>
            <SuggestionChip onClick={onSend} disabled={disabled}>What patients need attention?</SuggestionChip>
            <SuggestionChip onClick={onSend} disabled={disabled}>Who should I follow up with?</SuggestionChip>
          </div>
        )}

        {/* Textarea row — full width so placeholder starts at left */}
        <div style={{ padding: '10px 14px 0' }}>
          <textarea
            ref={textareaRef}
            value={value}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onKeyUp={syncCaret}
            onClick={syncCaret}
            aria-autocomplete="list"
            aria-expanded={pickerActive}
            placeholder="Ask Phanova AI about your patients and clinic"
            rows={1}
            disabled={disabled}
            style={{
              display: 'block',
              width: '100%',
              resize: 'none',
              border: 'none',
              outline: 'none',
              fontSize: 14,
              fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
              lineHeight: 1.5,
              color: '#0f172a',
              backgroundColor: 'transparent',
              maxHeight: 160,
              minHeight: 24,
              padding: 0,
            }}
          />
        </div>

        {/* Controls row */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          padding: '8px 10px 10px',
          gap: 8,
        }}>
          {/* Context / @ button */}
          <button
            type="button"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              padding: '4px 10px',
              borderRadius: 12,
              border: '1px solid #e2e8f0',
              backgroundColor: 'transparent',
              color: '#64748b',
              fontSize: 13,
              cursor: 'pointer',
              fontWeight: 500,
              whiteSpace: 'nowrap',
            }}
            onClick={() => {}}
            aria-label="Add context"
          >
            <span style={{ fontWeight: 600, color: '#1976d2' }}>@</span> Add context
          </button>

          <div style={{ flex: 1 }} />

          {/* Attachment button */}
          <button
            type="button"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 32,
              height: 32,
              borderRadius: '50%',
              border: 'none',
              backgroundColor: 'transparent',
              color: '#94a3b8',
              cursor: 'pointer',
              flexShrink: 0,
            }}
            aria-label="Attach file"
            title="Attach file"
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f1f5f9')}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
          >
            {/* Paperclip icon */}
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <path
                d="M12.5 7.5L6.5 13.5a2.12 2.12 0 1 1-3-3l6-6a2.83 2.83 0 0 1 4 4l-6 6a3.54 3.54 0 1 1-5-5l6-6"
                stroke="currentColor"
                strokeWidth="1.2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>

          {/* Stop button (PHA-1155) — replaces Send while an answer is in progress */}
          {onStop ? (
            <button
              type="button"
              onClick={() => {
                setStopRequested(true);
                onStop();
              }}
              disabled={stopRequested}
              title={stopRequested ? 'Stopping after the current step…' : 'Stop'}
              aria-label="Stop answer"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 32,
                height: 32,
                borderRadius: '50%',
                border: 'none',
                backgroundColor: stopRequested ? '#e2e8f0' : '#0f172a',
                color: '#fff',
                cursor: stopRequested ? 'default' : 'pointer',
                flexShrink: 0,
              }}
            >
              <span style={{ width: 10, height: 10, borderRadius: 2, backgroundColor: stopRequested ? '#94a3b8' : '#fff' }} />
            </button>
          ) : (
          <button
            type="button"
            onClick={handleSubmit}
            disabled={disabled || !value.trim()}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 32,
              height: 32,
              borderRadius: '50%',
              border: 'none',
              backgroundColor: value.trim() ? '#1976d2' : '#e2e8f0',
              color: value.trim() ? '#fff' : '#94a3b8',
              cursor: value.trim() ? 'pointer' : 'default',
              flexShrink: 0,
              transition: 'background-color 0.15s ease',
            }}
            aria-label="Send message"
          >
            <PaperPlaneIcon
              style={{
                width: 14,
                height: 14,
                transform: 'rotate(-45deg)',
              }}
            />
          </button>
          )}
        </div>
      </div>
    </div>
  );
};

// "No saved reports yet" links to /reports for designers; in a Router it is an
// SPA link, otherwise (isolated renders) a plain anchor.
const ReportsLink: React.FC = () => {
  const inRouter = useInRouterContext();
  const style = { color: '#1976d2', textDecoration: 'underline' };
  return inRouter
    ? <Link to="/reports" style={style}>Create one on the Reports page</Link>
    : <a href="/reports" style={style}>Create one on the Reports page</a>;
};

// PHA-1168: popover above the input listing saved reports. mouseDown (not click)
// picks, so the textarea keeps focus and its caret.
const ReportPicker: React.FC<{
  reports: ReportSummary[];
  activeIndex: number;
  loading: boolean;
  noReportsAtAll: boolean;
  canDesign: boolean;
  onHover: (index: number) => void;
  onPick: (report: ReportSummary) => void;
}> = ({ reports, activeIndex, loading, noReportsAtAll, canDesign, onHover, onPick }) => (
  <div
    role="listbox"
    aria-label="Saved reports"
    style={{
      position: 'absolute',
      left: 16,
      right: 16,
      bottom: '100%',
      marginBottom: 4,
      maxHeight: 260,
      overflowY: 'auto',
      background: '#fff',
      border: '1px solid #e2e8f0',
      borderRadius: 10,
      boxShadow: '0 4px 16px rgba(15,23,42,0.12)',
      zIndex: 5,
    }}
  >
    {reports.length === 0 ? (
      <div style={{ padding: '10px 14px', fontSize: 13, color: '#64748b' }}>
        {loading
          ? 'Loading reports…'
          : noReportsAtAll
            ? <>No saved reports yet.{canDesign && <> <ReportsLink /></>}</>
            : 'No report matches.'}
      </div>
    ) : reports.map((r, i) => (
      <div
        key={r.id}
        role="option"
        aria-selected={i === activeIndex}
        onMouseDown={(e) => { e.preventDefault(); onPick(r); }}
        onMouseEnter={() => onHover(i)}
        style={{
          padding: '8px 14px',
          cursor: 'pointer',
          background: i === activeIndex ? '#f1f5f9' : 'transparent',
          borderBottom: i < reports.length - 1 ? '1px solid #f1f5f9' : 'none',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 600, fontSize: 13, color: '#0f172a' }}>{r.name}</span>
          <span style={{ fontSize: 12, color: '#0369a1', fontFamily: 'ui-monospace, Menlo, monospace' }}>#{r.handle}</span>
        </div>
        {r.description && (
          <div style={{ fontSize: 12, color: '#64748b', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {r.description}
          </div>
        )}
      </div>
    ))}
  </div>
);

// Small suggestion chip above the textarea
const SuggestionChip: React.FC<{
  onClick: (text: string) => void;
  disabled?: boolean;
  children: string;
}> = ({ onClick, disabled, children }) => (
  <button
    type="button"
    onClick={() => !disabled && onClick(children)}
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: 4,
      padding: '4px 8px 4px 24px',
      borderRadius: 12,
      border: 'none',
      backgroundColor: '#f8fafc',
      color: '#475569',
      fontSize: 12,
      cursor: disabled ? 'default' : 'pointer',
      fontWeight: 400,
      lineHeight: 1.5,
      position: 'relative',
      opacity: disabled ? 0.5 : 1,
      transition: 'opacity 0.15s ease',
    }}
  >
    {/* Chat bubble icon */}
    <span style={{
      position: 'absolute',
      left: 8,
      top: '50%',
      transform: 'translateY(-45%)',
      width: 14,
      height: 14,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}>
      <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
        <path
          d="M2 3.5C2 2.67 2.67 2 3.5 2H12.5C13.33 2 14 2.67 14 3.5V9.5C14 10.33 13.33 11 12.5 11H5L2.5 13.5V11H3.5C2.67 11 2 10.33 2 9.5V3.5Z"
          stroke="#94a3b8"
          strokeWidth="1.2"
          fill="none"
        />
      </svg>
    </span>
    {children}
  </button>
);

export default ChatInput;
